import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { userApi } from '../../../api/adminapi';
import '../Admin.css';

function UserDetails() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [qrCodeData, setQrCodeData] = useState(null);
  const [isQrLoading, setIsQrLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [isLogsLoading, setIsLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState('');
  const [logsInfoMessage, setLogsInfoMessage] = useState('');

  useEffect(() => {
    if (userId) {
      fetchQRCode();
    }
  }, [userId]);

  const fetchQRCode = async () => {
    try {
      setIsQrLoading(true);
      const response = await userApi.getUserQRCode(userId);
      const qrBase64 = response?.data?.qrCodeBase64;
      const userFromQr = response?.data?.user;
      if (userFromQr) {
        setUser(prev => ({ ...(prev || {}), ...userFromQr }));
      }
      if (qrBase64) {
        setQrCodeData({
          qrCode: `data:image/png;base64,${qrBase64}`,
          generatedAt: new Date().toISOString()
        });
      }
      setIsLoading(false);
      setIsQrLoading(false);
    } catch (err) {
      console.error('Error generating QR code:', err);
      setIsLoading(false);
      setIsQrLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/community/users');
  };


  const handleDownloadQR = () => {
    if (qrCodeData?.qrCode) {
      const link = document.createElement('a');
      link.href = qrCodeData.qrCode;
      link.download = `user-${user?.empId}-qr-code.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleViewLogs = async () => {
    if (!user?.empId) return;
    try {
      setIsLogsLoading(true);
      setLogsError('');
      const response = await userApi.getUserLogs(user.empId);
      const payload = response?.data; // ResponseHandler puts array at data
      if (Array.isArray(payload)) {
        setLogs(payload);
        setLogsInfoMessage('');
      } else {
        setLogs([]);
        // Show backend message when no logs
        setLogsInfoMessage(response?.message);
      }
    } catch (err) {
      console.error('Error fetching user logs:', err);
      setLogsError('Failed to load logs');
    } finally {
      setIsLogsLoading(false);
    }
  };

  const backButton = (
    <button 
      className="admin-btn admin-btn-secondary"
      onClick={handleBack}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 12H5M12 19l-7-7 7-7"/>
      </svg>
      Back to Users
    </button>
  );

  if (isLoading) {
    return (
      <div className="admin-view user-details-view">
        <PageHeader 
          breadcrumbs={["Community", "Users", "User Details"]} 
          actionButton={backButton}
        />
        <div className="user-details-loading">
          <div className="loading-skeleton">
            <div className="skeleton-header"></div>
            <div className="skeleton-content">
              <div className="skeleton-row"></div>
              <div className="skeleton-row"></div>
              <div className="skeleton-row"></div>
              <div className="skeleton-row"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-view user-details-view">
        <PageHeader 
          breadcrumbs={["Community", "Users", "User Details"]} 
          actionButton={backButton}
        />
        <div className="error-state">
          <div className="error-message">{error}</div>
          <button 
            className="admin-btn admin-btn-primary"
            onClick={fetchQRCode}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-view user-details-view">
      <PageHeader 
        breadcrumbs={["Community", "Users", "User Details"]} 
        actionButton={backButton}
      />

      <div className="user-details-container">
        {/* User Information Card */}
        <div className="user-info-card card">
          <div className="user-header">
            <div className="user-avatar">
              <div className="avatar large">
                {user?.firstName?.[0] || '?'}
              </div>
            </div>
            <div className="user-basic-info">
              <h2 className="user-name">
                {`${user?.firstName || ''} ${user?.secondName || ''}`.trim()}
              </h2>
              <p className="user-role">{user?.roleName || user?.role || '-'}</p>
              <div className="user-status">
                <span className={`status-badge ${user?.status?.toLowerCase() || 'active'}`}>
                  {user?.status || 'ACTIVE'}
                </span>
              </div>
            </div>
          </div>

          <div className="user-details-grid">
            <div className="detail-item">
              <label>Employee ID</label>
              <span>{user?.empId || '-'}</span>
            </div>
            <div className="detail-item">
              <label>Email</label>
              <span>{user?.email || '-'}</span>
            </div>
            <div className="detail-item">
              <label>Phone</label>
              <span>{user?.phone || '-'}</span>
            </div>
            <div className="detail-item">
              <label>Location</label>
              <span>{user?.location || '-'}</span>
            </div>
            <div className="detail-item">
              <label>Department</label>
              <span>{user?.department || '-'}</span>
            </div>
            <div className="detail-item">
              <label>Assigned Vehicle</label>
              <span>{user?.assignedVehicle || '-'}</span>
            </div>
            <div className="detail-item">
              <label>Route</label>
              <span>{user?.assignedStation || '-'}</span>
            </div>
          </div>
        </div>

        {/* QR Code Card */}
        <div className="qr-code-card card">
          <div className="qr-header">
            <h3>QR Code</h3>
            <p>User identification QR code for quick access</p>
          </div>

          <div className="qr-content">
            {isQrLoading && (
              <div className="qr-loading">
                <div className="qr-skeleton">
                  <div className="skeleton-qr"></div>
                </div>
                <p>Generating QR Code...</p>
              </div>
            )}

            {qrCodeData && !isQrLoading && (
              <div className="qr-display">
                <div className="qr-image-container">
                  <img 
                    src={qrCodeData.qrCode} 
                    alt="User QR Code" 
                    className="qr-image"
                  />
                </div>
                <div className="qr-actions">
                  <button 
                    className="admin-btn admin-btn-primary"
                    onClick={handleDownloadQR}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7,10 12,15 17,10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Download QR Code
                  </button>
                </div>
                <div className="qr-meta">
                  <small>Generated on: {new Date(qrCodeData.generatedAt).toLocaleString()}</small>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* User Log Card */}
        <div className="card call-logs-card" style={{ marginTop: '16px' }}>
          <div className="qr-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h3>User Log</h3>
              <p>Recent scans/entries associated with this user</p>
            </div>
            <button className="admin-btn admin-btn-primary" onClick={handleViewLogs} disabled={!user?.empId || isLogsLoading}>
              {isLogsLoading ? 'Loading...' : 'View Log'}
            </button>
          </div>

          <div className="qr-content">
            {logsError && (
              <div className="error-message" style={{ marginBottom: '8px' }}>{logsError}</div>
            )}
            {logs?.length > 0 ? (
              <div className="table-responsive">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th className="text-center">#</th>
                      <th className="nowrap">Round</th>
                      <th className="nowrap">Scanned At</th>
                      <th className="nowrap">Assigned Vehicle</th>
                      <th className="nowrap">Traveling Vehicle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, index) => (
                      <tr key={log.id || index}>
                        <td className="text-center muted">{index + 1}</td>
                        <td className="nowrap">{log.round || '-'}</td>
                        <td className="nowrap">{log.scanAt ? new Date(log.scanAt).toLocaleString() : '-'}</td>
                        <td className="nowrap">{log.assignedVehicle || '-'}</td>
                        <td className="nowrap">{log.travelingVehicle || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <p>{logsInfoMessage || 'Currently no logs present for that user'}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserDetails;
